import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { MdContentCopy, MdPayment, MdRefresh } from 'react-icons/md'
import {
    getPayments,
    getPixQrCode,
    getSubscriptionPayments,
    Payment,
    SubscriptionPayment,
} from '@/lib/api/commerce/payments-api'
import { useAuth } from '@/store/hooks'
import { formatPrice, formatDateTime, getBillingStatusConfig } from '../account-utils'

/**
 * Aba de pagamentos: historico de cobrancas da assinatura e das compras.
 *
 * Carrega ao montar. Antes o disparo vinha do clique na aba
 * (`if (!paymentsLoaded) loadPayments()`), o que espalhava a regra de
 * carregamento pelo botao da aba; aqui montar ja e o sinal.
 */
export default function PaymentsTab() {
    const { user } = useAuth()
    const [loadingPayments, setLoadingPayments] = useState(false)
    const [payments, setPayments] = useState<Payment[]>([])
    const [paymentsLoaded, setPaymentsLoaded] = useState(false)

    const loadPayments = useCallback(async () => {
        const organizationId = user?.company?.organization?.id
        if (!user?.companyId || !organizationId) return

        setLoadingPayments(true)
        try {
            const [commerceRes, subscriptionRes] = await Promise.all([
                getPayments({ companyId: user.companyId }),
                getSubscriptionPayments(organizationId),
            ])

            const commercePayments =
                commerceRes.success && commerceRes.result
                    ? commerceRes.result
                    : []

            const subscriptionPayments =
                subscriptionRes.success && subscriptionRes.result
                    ? subscriptionRes.result.map(
                          (sp: SubscriptionPayment): Payment => {
                              const planPrice =
                                  sp.subscription?.plan?.customPrice ??
                                  sp.subscription?.plan?.price
                              const isPack = sp.description
                                  ? sp.description
                                        .toLowerCase()
                                        .includes('pack')
                                  : planPrice
                                    ? sp.amount !== planPrice
                                    : false

                              return {
                                  id: `sub-${sp.id}`,
                                  asaasPaymentId: sp.paymentId,
                                  companyId: 0,
                                  clientId: 0,
                                  value: sp.amount,
                                  billingType: (sp.billingType ||
                                      (sp.creditCardNumber
                                          ? 'CREDIT_CARD'
                                          : 'PIX')) as Payment['billingType'],
                                  status: (sp.status ||
                                      'PENDING') as Payment['status'],
                                  dueDate: sp.dateRef,
                                  paymentDate:
                                      sp.status === 'CONFIRMED' ||
                                      sp.status === 'RECEIVED'
                                          ? sp.updatedAt
                                          : undefined,
                                  creditCardNumber: sp.creditCardNumber,
                                  creditCardBrand: sp.creditCardBrand,
                                  description:
                                      sp.description ||
                                      (isPack
                                          ? 'Compra de pack'
                                          : `Assinatura - ${sp.subscription?.plan?.name || ''}`),
                                  createdAt: sp.createdAt,
                                  updatedAt: sp.updatedAt,
                              }
                          }
                      )
                    : []

            const allPayments = [
                ...commercePayments,
                ...subscriptionPayments,
            ].sort(
                (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
            )

            setPayments(allPayments)
            setPaymentsLoaded(true)
        } catch (error) {
            toast.error('Erro ao carregar pagamentos')
        } finally {
            setLoadingPayments(false)
        }
    }, [user?.companyId, user?.company?.organization?.id])

    const handleRefreshPix = async (paymentId: string) => {
        try {
            const response = await getPixQrCode(paymentId)
            if (response.success && response.result) {
                await loadPayments()
                toast.success('QR Code PIX atualizado')
            } else {
                toast.error(response.error || 'Erro ao atualizar QR Code')
            }
        } catch (error) {
            toast.error('Erro ao atualizar QR Code PIX')
        }
    }

    const handleCopyPix = (pixCode: string) => {
        navigator.clipboard.writeText(pixCode)
        toast.success('Código PIX copiado!')
    }

    useEffect(() => {
        void loadPayments()
    }, [loadPayments])

    return (
            <div className="flex flex-col gap-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-base-content">
                        Histórico de Pagamentos
                    </h2>
                    <button
                        className="btn btn-sm btn-outline"
                        onClick={() => {
                            setPaymentsLoaded(false)
                            loadPayments()
                        }}
                        disabled={loadingPayments}
                    >
                        {loadingPayments ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <>
                                <MdRefresh className="text-lg" />
                                Atualizar
                            </>
                        )}
                    </button>
                </div>

                {loadingPayments && !paymentsLoaded ? (
                    <div className="flex items-center justify-center min-h-[400px]">
                        <span className="loading loading-spinner loading-lg"></span>
                    </div>
                ) : payments.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 text-center p-8">
                        <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                            <MdPayment className="text-4xl text-base-content/30" />
                        </div>
                        <h3 className="text-xl font-semibold text-base-content">
                            Nenhum pagamento encontrado
                        </h3>
                        <p className="text-sm text-base-content/60 max-w-md">
                            Você ainda não possui pagamentos
                            registrados.
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4">
                        {payments.map(payment => {
                            const statusConfig =
                                getBillingStatusConfig(payment.status)
                            const StatusIcon = statusConfig.icon
                            const isPix =
                                payment.billingType === 'PIX'
                            const isPending =
                                payment.status === 'PENDING'
                            const canRefreshPix =
                                isPix && isPending

                            return (
                                <div
                                    key={payment.id}
                                    className="card bg-base-200/50 border border-base-300"
                                >
                                    <div className="card-body p-4">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-2">
                                                    <div
                                                        className={`badge badge-lg gap-2 ${statusConfig.className}`}
                                                    >
                                                        <StatusIcon className="text-base" />
                                                        {
                                                            statusConfig.label
                                                        }
                                                    </div>
                                                    <span className="badge badge-outline">
                                                        {payment.billingType ===
                                                        'CREDIT_CARD'
                                                            ? 'Cartão de Crédito'
                                                            : payment.billingType ===
                                                                'PIX'
                                                              ? 'PIX'
                                                              : payment.billingType ===
                                                                  'BOLETO'
                                                                ? 'Boleto'
                                                                : payment.billingType}
                                                    </span>
                                                </div>
                                                <p className="text-lg font-bold text-base-content">
                                                    {formatPrice(
                                                        payment.value
                                                    )}
                                                </p>
                                                {payment.description && (
                                                    <p className="text-sm text-base-content/70 mt-1">
                                                        {
                                                            payment.description
                                                        }
                                                    </p>
                                                )}
                                                <div className="flex flex-wrap gap-4 mt-3 text-xs text-base-content/60">
                                                    <span>
                                                        <strong>
                                                            Vencimento:
                                                        </strong>{' '}
                                                        {formatDateTime(
                                                            payment.dueDate
                                                        )}
                                                    </span>
                                                    {payment.paymentDate && (
                                                        <span>
                                                            <strong>
                                                                Pago
                                                                em:
                                                            </strong>{' '}
                                                            {formatDateTime(
                                                                payment.paymentDate
                                                            )}
                                                        </span>
                                                    )}
                                                    {payment.order && (
                                                        <span>
                                                            <strong>
                                                                Pedido:
                                                            </strong>{' '}
                                                            #
                                                            {
                                                                payment
                                                                    .order
                                                                    .id
                                                            }
                                                        </span>
                                                    )}
                                                </div>
                                                {payment.creditCardNumber && (
                                                    <p className="text-xs text-base-content/60 mt-2">
                                                        Cartão:{' '}
                                                        {
                                                            payment.creditCardBrand
                                                        }{' '}
                                                        ••••{' '}
                                                        {
                                                            payment.creditCardNumber
                                                        }
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {isPix && isPending && (
                                            <div className="mt-4 pt-4 border-t border-base-300">
                                                {payment.pixCopyPaste ? (
                                                    <div className="flex flex-col gap-2">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-sm font-semibold text-base-content">
                                                                Código
                                                                PIX:
                                                            </span>
                                                            <button
                                                                className="btn btn-xs btn-ghost"
                                                                onClick={() =>
                                                                    handleCopyPix(
                                                                        payment.pixCopyPaste ||
                                                                            ''
                                                                    )
                                                                }
                                                            >
                                                                <MdContentCopy className="text-sm" />
                                                                Copiar
                                                            </button>
                                                        </div>
                                                        <div className="bg-base-100 p-3 rounded-lg border border-base-300 break-all text-xs font-mono">
                                                            {
                                                                payment.pixCopyPaste
                                                            }
                                                        </div>
                                                        {payment.pixQrCodeBase64 && (
                                                            <div className="flex justify-center mt-2">
                                                                <img
                                                                    src={`data:image/png;base64,${payment.pixQrCodeBase64}`}
                                                                    alt="QR Code PIX"
                                                                    className="max-w-[200px]"
                                                                />
                                                            </div>
                                                        )}
                                                        {canRefreshPix && (
                                                            <button
                                                                className="btn btn-sm btn-outline w-full mt-2"
                                                                onClick={() =>
                                                                    handleRefreshPix(
                                                                        payment.id
                                                                    )
                                                                }
                                                            >
                                                                <MdRefresh className="text-lg" />
                                                                Atualizar
                                                                QR
                                                                Code
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col gap-2">
                                                        <p className="text-sm text-base-content/70">
                                                            QR
                                                            Code
                                                            PIX
                                                            não
                                                            disponível
                                                        </p>
                                                        {canRefreshPix && (
                                                            <button
                                                                className="btn btn-sm btn-secondary"
                                                                onClick={() =>
                                                                    handleRefreshPix(
                                                                        payment.id
                                                                    )
                                                                }
                                                            >
                                                                <MdRefresh className="text-lg" />
                                                                Obter
                                                                QR
                                                                Code
                                                                PIX
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {payment.invoiceUrl && (
                                            <div className="mt-3">
                                                <a
                                                    href={
                                                        payment.invoiceUrl
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-sm btn-link"
                                                >
                                                    Ver Fatura
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
    )
}
